import { useTheme } from "expo-router/react-navigation";
import { ComponentRef, FC, useMemo, useRef, useState } from "react";
import { View } from "react-native";

import {
  BarChart,
  GroupedBarChart,
  LinearScaleGauge,
} from "@/components/charts";
import { LinearScaleGaugeRef } from "@/components/charts/LinearScaleGauge";
import { Card, HStack, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { QuestionResults } from "@/types/resources";
import ChartCopyButton from "./ChartCopyButton";
import { copyChartToClipboard } from "./copyChartToClipboard";

/* ── Types ─────────────────────────────────────────────── */

interface LinearScaleQuestionResultsProps {
  compact?: boolean;
  result: QuestionResults;
  selectedGroupIndex: "all" | string;
}

/* ── Component ──────────────────────────────────────────── */

const LinearScaleQuestionResults: FC<LinearScaleQuestionResultsProps> = ({
  compact = false,
  result,
  selectedGroupIndex,
}) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const { groups, question } = result;
  const isSingleGroup = groups.length === 1;

  const gaugeRef = useRef<LinearScaleGaugeRef>(null);
  const chartRef = useRef<ComponentRef<typeof View>>(null);
  const [gaugeCopied, setGaugeCopied] = useState(false);
  const [chartCopied, setChartCopied] = useState(false);

  const scaleSize = question.options.length;
  const scale: string[] = question.options;

  const totalRespondents = useMemo(
    () =>
      groups.reduce(
        (sum, g) =>
          sum +
          g.answers.filter(
            ({ answer }) =>
              answer.type === "linear_scale" && answer.rating !== null
          ).length,
        0
      ),
    [groups]
  );

  const averageRating = useMemo(() => {
    const all = groups
      .flatMap((g) => g.answers)
      .filter(
        ({ answer }) => answer.type === "linear_scale" && answer.rating !== null
      )
      .map(({ answer }) =>
        answer.type === "linear_scale" ? answer.rating : null
      )
      .filter((r): r is number => r !== null);
    return all.length > 0 ? all.reduce((s, r) => s + r, 0) / all.length : null;
  }, [groups]);

  const barChartPoints = useMemo(() => {
    if (!isSingleGroup) return [];
    const counts = Array.from({ length: scaleSize }, () => 0);
    groups[0].answers.forEach(({ answer }) => {
      if (answer.type === "linear_scale" && answer.rating !== null)
        counts[answer.rating - 1]++;
    });
    return counts.map((y, idx) => ({ x: String(idx + 1), y }));
  }, [groups, isSingleGroup, scaleSize]);

  const barChartGroups = useMemo(() => {
    if (isSingleGroup) return [];
    return groups.map((group, groupIdx) => {
      const counts = Array.from({ length: scaleSize }, () => 0);
      group.answers.forEach(({ answer }) => {
        if (answer.type === "linear_scale" && answer.rating !== null)
          counts[answer.rating - 1]++;
      });
      return {
        key: group.group.id || `group-${groupIdx}`,
        label:
          group.group.name ||
          group.group.panel?.name ||
          (i18n.t("studies.group", { index: groupIdx + 1 }) as string),
        points: counts.map((y, idx) => ({ x: String(idx + 1), y })),
        respondentCount: group.answers.filter(
          ({ answer }) =>
            answer.type === "linear_scale" && answer.rating !== null
        ).length,
      };
    });
  }, [groups, i18n, isSingleGroup, scaleSize]);

  const copyGauge = async () => {
    if (!gaugeRef.current) return;
    await gaugeRef.current.copy();
    setGaugeCopied(true);
    setTimeout(() => setGaugeCopied(false), 2000);
  };

  const copyChart = async () => {
    const element = chartRef.current as unknown as HTMLElement;
    if (!element) return;
    await copyChartToClipboard(element, () => {
      setChartCopied(true);
      setTimeout(() => setChartCopied(false), 2000);
    });
  };

  return (
    <HStack className="gap-3 items-stretch flex-col lg:flex-row">
      {/* Card 1 — Average score gauge. */}
      <Card
        className={`group rounded-2xl w-full min-w-0 lg:flex-1 ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 p-6"}`}
      >
        <ChartCopyButton
          compact={compact}
          copied={gaugeCopied}
          onPress={copyGauge}
          textColor={theme.colors.text}
        />
        <VStack className="gap-4 flex-1">
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
            {i18n.t("questions.averageScore", { count: totalRespondents })}
          </Text>
          <View
            style={{
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              width: "100%",
            }}
          >
            <LinearScaleGauge
              rating={averageRating}
              ref={gaugeRef}
              scale={scale}
            />
          </View>
        </VStack>
      </Card>

      {/* Card 2 — Score distribution bar chart. */}
      <Card
        className={`group rounded-2xl w-full min-w-0 lg:flex-1 ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 px-6 pt-6 pb-2"}`}
      >
        <ChartCopyButton
          compact={compact}
          copied={chartCopied}
          onPress={copyChart}
          textColor={theme.colors.text}
        />
        <VStack className="gap-6 flex-1">
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
            {i18n.t("dashboardNavigator.chartTitleScoreDistribution", {
              count: totalRespondents,
            })}
          </Text>
          <View
            ref={chartRef}
            style={{
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              width: "100%",
            }}
          >
            {isSingleGroup ? (
              <BarChart
                minWidth={selectedGroupIndex === "all" ? 600 : 400}
                points={barChartPoints}
                totalRespondents={totalRespondents}
              />
            ) : (
              <GroupedBarChart
                groups={barChartGroups}
                minWidth={selectedGroupIndex === "all" ? 600 : 400}
              />
            )}
          </View>
        </VStack>
      </Card>
    </HStack>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default LinearScaleQuestionResults;
